import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useHorarios } from '../hooks/useHorarios';
import { useExcepcionesValidation } from '../hooks/useExcepcionesValidation';
import {
  HorariosHeader,
  HorariosTable,
  ModalCrearHorario,
  ExcepcionesCalendario,
  TablaExcepciones,
  ModalEditarExcepcion
} from '../components/Horarios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const accesoRestringidoStyles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  card: { backgroundColor: 'white', padding: '2rem 3rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' },
  icon: { fontSize: '4rem', display: 'block', marginBottom: '1rem' },
  message: { color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' },
  button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' },
};

const HorariosCompleto = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [creandoHorario, setCreandoHorario] = useState(false);
  const [tab, setTab] = useState('horario-base');

  // Excepciones
  const [showEditarExcepcionModal, setShowEditarExcepcionModal] = useState(false);
  const [excepcionEditando, setExcepcionEditando] = useState(null);
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date());

  // Hook personalizado
  const {
    sucursales,
    horarios,
    sucursalSeleccionada,
    horarioSeleccionado,
    loading,
    diasBase,
    excepciones,
    excepcionesCalendario,
    setSucursalSeleccionada,
    setHorarioSeleccionado,
    setExcepciones,
    setExcepcionesCalendario,
    fetchSucursales,
    fetchHorarios,
    fetchHorarioCompleto,
    fetchExcepcionesMes,
    crearHorario,
    actualizarHorario,
    agregarExcepcion,
    actualizarExcepcion,
    eliminarExcepcion
  } = useHorarios();

  // Hook validaciones
  const {
    validarHoras,
    combinarExcepciones
  } = useExcepcionesValidation();

  const tieneSucursales = user?.tenant?.tiene_sucursales;

  // Verificar acceso
  useEffect(() => {
    if (authLoading) return;

    if (user?.tenant?.acceso_bloqueado === true) {
      setAccesoDenegado(true);
      setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
      return;
    }

    if (user?.rol !== 'admin') {
      setAccesoDenegado(true);
      setMensajeError('Solo los administradores pueden gestionar horarios.');
      return;
    }

    fetchSucursales();
  }, [authLoading, user?.tenant?.acceso_bloqueado, user?.rol]);

  // Cargar horarios cuando cambia sucursal
  useEffect(() => {
    if (sucursalSeleccionada) {
      fetchHorarios(sucursalSeleccionada);
    }
  }, [sucursalSeleccionada]);

  // Cargar detalles cuando se selecciona horario
  useEffect(() => {
    if (horarioSeleccionado) {
      fetchHorarioCompleto(horarioSeleccionado);
    }
  }, [horarioSeleccionado]);

  // Cargar excepciones cuando cambia tab o mes
  useEffect(() => {
    if (horarioSeleccionado && tab === 'excepciones') {
      fetchExcepcionesMes(horarioSeleccionado, mesSeleccionado).then(data => {
        setExcepciones(data);
        const combinadas = combinarExcepciones(data, []);
        setExcepcionesCalendario(combinadas);
      });
    }
  }, [horarioSeleccionado, tab, mesSeleccionado]);

  const handleCrearHorario = async (formData) => {
    setCreandoHorario(true);
    try {
      await crearHorario(formData);
      setShowCrearModal(false);
    } catch (error) {
      // Error manejado por hook
    } finally {
      setCreandoHorario(false);
    }
  };

  const handleActualizarDia = async (diaId, formData) => {
    try {
      const diasActualizados = diasBase.map(d =>
        d.id === diaId
          ? {
              dia_semana: d.dia_semana,
              hora_apertura: formData.hora_apertura ?? d.hora_apertura,
              hora_cierre: formData.hora_cierre ?? d.hora_cierre,
              esta_abierto: formData.esta_abierto ?? d.esta_abierto,
            }
          : {
              dia_semana: d.dia_semana,
              hora_apertura: d.hora_apertura,
              hora_cierre: d.hora_cierre,
              esta_abierto: d.esta_abierto,
            }
      );
      await actualizarHorario(horarioSeleccionado, { dias: diasActualizados });
      await fetchHorarioCompleto(horarioSeleccionado);
    } catch (error) {
      toast.error('Error al actualizar horario');
    }
  };

  const handleAgregarExcepcion = async (fecha) => {
    setExcepcionEditando({ fecha });
    setShowEditarExcepcionModal(true);
  };

  const handleEditarExcepcion = (excepcion) => {
    setExcepcionEditando(excepcion);
    setShowEditarExcepcionModal(true);
  };

  const handleGuardarExcepcion = async (formData) => {
    try {
      if (excepcionEditando?.id) {
        await actualizarExcepcion(excepcionEditando.id, formData);
      } else if (Array.isArray(formData)) {
        for (const bloque of formData) {
          await agregarExcepcion(horarioSeleccionado, { ...bloque, fecha: excepcionEditando.fecha });
        }
      } else {
        await agregarExcepcion(horarioSeleccionado, { ...formData, fecha: excepcionEditando.fecha });
      }
      setShowEditarExcepcionModal(false);
      setExcepcionEditando(null);
      // Recargar excepciones
      await fetchExcepcionesMes(horarioSeleccionado, mesSeleccionado);
    } catch (error) {
      toast.error('Error al guardar excepción');
    }
  };

  const handleEliminarExcepcion = async (excepcionId) => {
    try {
      await eliminarExcepcion(excepcionId);
      // Recargar excepciones
      await fetchExcepcionesMes(horarioSeleccionado, mesSeleccionado);
    } catch (error) {
      toast.error('Error al eliminar excepción');
    }
  };

  // Acceso denegado
  if (accesoDenegado) {
    return (
      <Layout>
        <div style={accesoRestringidoStyles.container}>
          <div style={accesoRestringidoStyles.card}>
            <span style={accesoRestringidoStyles.icon}>🔐</span>
            <h2 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>Acceso Denegado</h2>
            <p style={accesoRestringidoStyles.message}>{mensajeError}</p>
            <button style={accesoRestringidoStyles.button} onClick={() => navigate('/')}>
              Volver al Inicio
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Cargando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem' }}>
          ⏰ Gestión de Horarios
        </h1>

        {/* Header */}
        <HorariosHeader
          sucursales={sucursales}
          horarios={horarios}
          sucursalSeleccionada={sucursalSeleccionada}
          horarioSeleccionado={horarioSeleccionado}
          onSucursalChange={setSucursalSeleccionada}
          onHorarioChange={setHorarioSeleccionado}
          onCrearClick={() => setShowCrearModal(true)}
          loading={loading}
          tieneSucursales={tieneSucursales}
        />

        {/* Tabs */}
        {horarioSeleccionado && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              display: 'flex',
              gap: '1rem',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <button
                onClick={() => setTab('horario-base')}
                style={{
                  padding: '1rem 1.5rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontWeight: tab === 'horario-base' ? '700' : '500',
                  color: tab === 'horario-base' ? '#2563eb' : '#6b7280',
                  borderBottom: tab === 'horario-base' ? '3px solid #2563eb' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📅 Horario Base
              </button>
              <button
                onClick={() => setTab('excepciones')}
                style={{
                  padding: '1rem 1.5rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontWeight: tab === 'excepciones' ? '700' : '500',
                  color: tab === 'excepciones' ? '#2563eb' : '#6b7280',
                  borderBottom: tab === 'excepciones' ? '3px solid #2563eb' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                🚫 Excepciones
              </button>
            </div>
          </div>
        )}

        {/* Tab: Horario Base */}
        {tab === 'horario-base' && horarioSeleccionado && diasBase.length > 0 && (
          <HorariosTable
            diasBase={diasBase}
            onActualizar={handleActualizarDia}
            loading={loading}
          />
        )}

        {/* Tab: Excepciones */}
        {tab === 'excepciones' && horarioSeleccionado && (
          <>
            {/* Calendario */}
            <ExcepcionesCalendario
              mesSeleccionado={mesSeleccionado}
              excepcionesCalendario={excepcionesCalendario}
              onMesChange={setMesSeleccionado}
              onDiaClick={handleAgregarExcepcion}
              loading={loading}
            />

            {/* Tabla de excepciones */}
            <TablaExcepciones
              excepciones={excepciones}
              onEditar={handleEditarExcepcion}
              onEliminar={handleEliminarExcepcion}
              loading={loading}
            />
          </>
        )}

        {/* Modal crear horario */}
        <ModalCrearHorario
          show={showCrearModal}
          onClose={() => setShowCrearModal(false)}
          onSubmit={handleCrearHorario}
          sucursales={sucursales}
          horarios={horarios}
          loading={creandoHorario}
        />

        {/* Modal editar excepción */}
        <ModalEditarExcepcion
          show={showEditarExcepcionModal}
          excepcion={excepcionEditando}
          excepcionesDelDia={excepciones.filter(e => e.fecha.split('T')[0] === excepcionEditando?.fecha && e.id !== excepcionEditando?.id)}
          onSubmit={handleGuardarExcepcion}
          onClose={() => {
            setShowEditarExcepcionModal(false);
            setExcepcionEditando(null);
          }}
          loading={loading}
          validarHoras={validarHoras}
        />
      </div>
    </Layout>
  );
};

export default HorariosCompleto;
